/**
 * Utility functions for PDF detection and handling
 */

/**
 * Check if a URL points to a PDF file
 */
export const isPDFUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Check for common PDF patterns
  const pdfPatterns = [
    /\.pdf$/i,           // Direct PDF file
    /\.pdf\?/i,          // PDF with query parameters
    /\.pdf#/i,           // PDF with fragment
    /\/pdf\//i,          // Path contains /pdf/
    /type=application\/pdf/i,  // MIME type in URL
    /format=pdf/i,       // Format parameter
    /\.pdf&/i           // PDF in query string
  ];
  
  return pdfPatterns.some(pattern => pattern.test(url));
};

/**
 * Generate PDF.js viewer URL for a given PDF URL
 */
export const getPDFViewerUrl = (pdfUrl: string): string => {
  // Parse the URL to extract page information
  let cleanUrl = pdfUrl;
  let pageNumber = null;
  
  // Check for page fragment in the URL (#page=44, #page:44, etc.)
  const pageMatch = pdfUrl.match(/#page[=:](\d+)/i);
  if (pageMatch) {
    pageNumber = pageMatch[1];
    cleanUrl = pdfUrl.replace(/#page[=:]\d+/i, ''); // Remove page fragment from URL
  }
  
  // Use our backend as a proxy to avoid CORS issues
  const proxyUrl = `/api/proxy-pdf?url=${encodeURIComponent(cleanUrl)}`;
  const encodedProxyUrl = encodeURIComponent(proxyUrl);
  
  // Build the viewer URL with parameters
  let viewerUrl = `/pdfjs/web/viewer.html?file=${encodedProxyUrl}&disableCreateObjectURL=false&isEvalSupported=false&verbosity=1&disableRange=true&disableAutoFetch=true`;
  
  // Add page parameter if found
  if (pageNumber) {
    viewerUrl += `&page=${pageNumber}`;
  }
  
  return viewerUrl;
};

/**
 * Check if a citation represents a PDF document
 * Detection is based solely on URL patterns to avoid false positives
 */
export const isPDFCitation = (citation: { url: string; title?: string; content?: string }): boolean => {
  return isPDFUrl(citation.url);
};
